<template>
    <div>
        <v-card flat>
            <template v-if="showTargetInstanceSelector">
                <p class="caption">INVITE TO</p>
                <v-select
                    v-model="selectedInstance"
                    :loading="fetchingInstances"
                    :disabled="fetchingInstances"
                    :items="instanceNames"
                    :menu-props="{ bottom: true, offsetY: true }"
                    label="Target instance"
                    hide-details
                    outlined
                    dense
                    class="mb-4"></v-select>
            </template>

            <v-row v-if="selectedInstanceId">
                <v-col>
                    <p class="caption">ACCESS ROLE</p>

                    <v-radio-group :disabled="numInvitationsToSend !== 0" v-model="invitationType" class="mt-0 pt-0">
                        <v-container fluid class="pa-0">
                            <v-row no-gutters>
                                <v-col v-for="mode in invitationTypes" :key="mode.type" cols="12" class="my-1">
                                    <v-card flat :color="invitationType === mode.value ? 'grey lighten-3' : ''" outlined>
                                        <v-radio :value="mode.value" class="pl-4">
                                            <template v-slot:label>
                                                <v-card-text>
                                                    <div class="text-button">
                                                        <b>{{ mode.type }}</b>
                                                    </div>
                                                    {{ mode.description }}
                                                </v-card-text>
                                            </template>
                                        </v-radio>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-radio-group>

                    <InviteEmailsInput
                        v-if="invitationType"
                        v-model="emailsInput"
                        :valid.sync="valid"
                        :disabled="numInvitationsToSend !== 0"
                        :showGenerateLinkOnlyToggle="true"
                        :generateInvitationLinksOnly.sync="generateInvitationLinksOnly"
                        :invitationErrorState="{
                            showDialog: showInvitationErrorsDialog,
                            errorType: validationErrorType,
                            invalidEmails: invalidEmailsList,
                            foundInvalidEmails,
                            backendError,
                            emailsValidationError
                        }"
                        @input="updateEmailString"
                        @proceedWithTheInvitation="inviteUsers($event.value)"
                        @validateEmailsAgain="validateEmailsAndInviteUsers($event.value)" />
                </v-col>
            </v-row>

            <v-card-actions v-if="showInviteAction || error">
                <div>
                    <v-alert v-if="error" color="error" icon="warning" class="mt-4 w-100" text>
                        <div class="d-flex flex-column">
                            <span class="font-weight-bold">Invitation Failure Error</span>
                            Failed to invite the following users
                            <ul>
                                <li v-for="(email, index) in failedInvitations" :key="index">
                                    {{ email }}
                                </li>
                            </ul>
                            <span v-if="invitationErrorContent">{{ invitationErrorContent }}</span>
                        </div>
                    </v-alert>
                    <v-btn
                        v-if="showInviteAction"
                        :loading="numInvitationsToSend > 0 || inviting || validatingEmails"
                        @click="validateEmailsAndInviteUsers(true)"
                        :disabled="!valid || !emailsInput || numInvitationsToSend > 0 || validatingEmails"
                        color="primary"
                        text>
                        <v-icon small>add</v-icon>
                        Users
                    </v-btn>
                </div>
            </v-card-actions>
        </v-card>
    </div>
</template>

<script>
import { splitEmails, sortArray } from '@/utils'
import { emailValidation } from '@/mixins/validateEmail'
import { mapGetters, mapState } from 'vuex'
import { enumsData } from '@/mixins/enums'
import InviteEmailsInput from '@/components/InviteEmailsInput.vue'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as instanceErrors from '@/modules/instance/errors'
import * as invitationsErrors from '@/mixins/errors/invitations'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'

const { getBackendErrorMessage } = createBackendErrorHandler(instanceErrors, invitationsErrors, lifecycleErrors, permissionsErrors)

export default {
    props: {
        dataForInstanceToInviteTo: { type: Object, default: null },
        showInviteAction: {
            type: Boolean,
            default: true
        },
        spaceId: {
            type: [Number, String],
            default: null
        },
        spaceType: {
            type: Number,
            default: null
        }
    },
    components: { InviteEmailsInput },
    mixins: [emailValidation, enumsData],
    data() {
        return {
            error: false,
            emailsInput: '',
            invitationType: 'instanceEditors',
            errorContent: 'An error has occurred',
            emailList: [],
            failedInvitations: [],
            invitationErrorContent: '',
            inviting: false,
            valid: false,
            numInvitationsToSend: 0,
            instanceMenu: false,
            selectedInstance: '',
            instanceSearch: '',
            tabValues: {
                INSTANCE_EDITOR_TAB: 'instanceEditors',
                INSTANCE_VIEWER_TAB: 'instanceViewers'
            },
            generateInvitationLinksOnly: false,
            invitationTypes: [
                {
                    type: 'Editors',
                    description:
                        'The invitee will be able to modify the contents of the specified instance and start applications there, but will not be able to invite other people or create new instances.',
                    value: 'instanceEditors'
                },
                {
                    type: 'Viewers',
                    description:
                        'The invitee will be able to read or distribute the contents of the instance to other spaces, but will not be able to start an application or invite people/create new instances.',
                    value: 'instanceViewers'
                }
            ]
        }
    },
    computed: {
        ...mapState('spaceStore', ['spaceInstances', 'fetchingInstances']),
        ...mapGetters('spaceStore', { routeSpaceType: 'currentSpaceType' }),
        resolvedSpaceType() {
            return this.spaceType === null ? this.routeSpaceType : this.spaceType
        },
        showTargetInstanceSelector() {
            return !this.dataForInstanceToInviteTo
        },
        instanceNames() {
            if (!this.spaceInstances) {
                return []
            }
            const instances =
                this.resolvedSpaceType === this.spaceTypes.RESEARCH_SPACE
                    ? this.spaceInstances.filter(instance => instance.long_id !== this.instanceFixedNames.DISTRIBUTED_INSTANCE)
                    : this.spaceInstances
            return sortArray(instances, 'long_id', 'ascending', true).map(instance => instance.long_id)
        },
        validationErrorType() {
            if (this.foundInvalidEmails) {
                return this.emailValidationErrorTypes.INVALID_EMAILS_ERROR
            } else if (this.backendError || this.emailsValidationError) {
                return this.emailValidationErrorTypes.EMAIL_VALIDATION_BACKEND_ERROR
            }
            return null
        },
        selectedInstanceId() {
            if (this.selectedInstance && this.spaceInstances) {
                return this.spaceInstances.find(instance => instance.long_id === this.selectedInstance)?.iid || null
            }
            return this.$route.params.iid || null
        },
        showInvitationErrorsDialog() {
            if (this.foundInvalidEmails || this.backendError || this.emailsValidationError) {
                return true
            }
            return false
        }
    },
    mounted() {
        if (this.dataForInstanceToInviteTo) {
            this.selectedInstance = this.dataForInstanceToInviteTo.instanceName
        } else {
            this.selectedInstance = this.instanceFixedNames.MASTER_INSTANCE
        }
        this.emitInviteActionState()
    },
    watch: {
        dataForInstanceToInviteTo: function (nextVal, preVal) {
            if (nextVal && nextVal.instanceName) {
                this.selectedInstance = nextVal.instanceName
            }
        },
        valid() {
            this.emitInviteActionState()
        },
        emailsInput() {
            this.emitInviteActionState()
        },
        selectedInstance() {
            this.emitInviteActionState()
        },
        numInvitationsToSend() {
            this.emitInviteActionState()
        },
        inviting() {
            this.emitInviteActionState()
        },
        validatingEmails() {
            this.emitInviteActionState()
        }
    },
    methods: {
        emitInviteActionState() {
            this.$emit('invite-action-state', {
                visible: true,
                loading: this.numInvitationsToSend > 0 || this.inviting || this.validatingEmails,
                disabled: !this.selectedInstanceId || !this.valid || !this.emailsInput || this.numInvitationsToSend > 0 || this.validatingEmails
            })
        },
        triggerInviteFromParent() {
            this.validateEmailsAndInviteUsers(true)
        },
        updateEmailString: function () {
            this.validatedEmails = []
            const emails = splitEmails(this.emailsInput)
            this.emailList = emails.map(email => email.email)
        },
        inviteUsers: function (confirmed) {
            if (confirmed) {
                this.error = false
                this.invitationErrorContent = ''
                let apiURL
                if (this.invitationType === this.tabValues.INSTANCE_EDITOR_TAB) {
                    apiURL = '/invitations/invite_instance_editor/' + this.selectedInstanceId
                } else if (this.invitationType === this.tabValues.INSTANCE_VIEWER_TAB) {
                    apiURL = '/invitations/invite_instance_viewer/' + this.selectedInstanceId
                }
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Inviting users...',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'info'
                })
                this.inviting = true
                this.numInvitationsToSend = this.emailList.length
                this.emailList.forEach(email => {
                    const postBody = { requestee_email: email, generate_link_only: this.generateInvitationLinksOnly }
                    this.$axios
                        .post(apiURL, postBody, { timeout: 300000 })
                        .then(response => {})
                        .catch(error => {
                            this.failedInvitations.push(email)
                            this.error = true
                            const invitationErrorContent = getBackendErrorMessage(error, '')
                            if (invitationErrorContent) this.invitationErrorContent = invitationErrorContent
                        })
                        .finally(() => {
                            this.numInvitationsToSend -= 1
                            if (this.numInvitationsToSend === 0) {
                                this.inviting = false
                                this.emailsInput = ''
                                this.$store.dispatch('spaceStore/fetchSpaceInvitations', this.spaceId || this.$route.params.sid)
                                if (!this.error) {
                                    this.$store.dispatch('showSnackBar', {
                                        snackBarText: 'Invitations have been successfully sent. Invited users will receive an email to accept the invitation.',
                                        snackBarTimeout: 5000,
                                        snackBarIcon: 'check_circle'
                                    })
                                    this.$emit('invite-success')
                                }
                            }
                        })
                })
            }
        },
        validateEmailsAndInviteUsers: function (confirmed) {
            if (confirmed) {
                this.validateEmails(this.emailList).finally(() => {
                    if (!this.emailsValidationError && !this.backendError && !this.foundInvalidEmails) {
                        this.inviteUsers(true)
                    }
                })
            }
        }
    }
}
</script>
